/** @jsxImportSource @emotion/react */
import { AIResponse, Box, Button, Icon, Text } from '@landtrustinc/design-system';
import React from 'react';

import type { ThrottledMessage } from '../../types';
import { containerStyles, iconStyles, statusStyles } from './styles';

type ThrottledProps = {
  msg: ThrottledMessage;
  isConsecutiveAssistant?: boolean;
  cancelQueuedRequest: () => void;
  retryLastMessage: () => void;
};

const Throttled: React.FC<ThrottledProps> = ({
  msg,
  isConsecutiveAssistant = false,
  cancelQueuedRequest,
  retryLastMessage,
}) => {
  return (
    <AIResponse
      key={msg.id}
      showDisclaimer={false}
      showHelpfulQuestion={false}
      showTitle={!isConsecutiveAssistant}
    >
      <Box css={containerStyles}>
        {/* Warning icon */}
        <Box css={iconStyles}>
          <Icon variant="Clock" size="medium" />
        </Box>

        {/* Message */}
        <Box mt="var(--spacing-2)">
          <Text size="md" fontWeight="semibold">
            {msg.throttleMessage}
          </Text>
        </Box>

        {/* Retry status */}
        <Box mt="var(--spacing-3)" css={statusStyles}>
          {msg.isAutoRetrying ? (
            <Text size="sm" color="var(--text-secondary)">
              Automatically retrying in {msg.retryAfter} seconds... (attempt {msg.retryCount}/
              {msg.maxRetries})
            </Text>
          ) : (
            <Text size="sm" color="var(--text-secondary)">
              Maximum retries reached. Please try again.
            </Text>
          )}
        </Box>

        {/* Action buttons */}
        <Box mt="var(--spacing-3)" display="flex" gap="var(--spacing-2)">
          {msg.isAutoRetrying ? (
            /* Cancel button during auto-retry */
            <Button size="sm" variant="secondary" onClick={cancelQueuedRequest}>
              Cancel
            </Button>
          ) : (
            /* Manual retry button when auto-retry is exhausted */
            <Button size="sm" variant="secondary" onClick={retryLastMessage}>
              Try Again
            </Button>
          )}
        </Box>
      </Box>
    </AIResponse>
  );
};

export default Throttled;
